import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Check, Search, X } from 'lucide-react';
import { clsx } from 'clsx';

type Id = number | string;
export type MultiSelectOption<T extends Id = number> = { id: T; name: string };

interface Props<T extends Id = number> {
  options: MultiSelectOption<T>[];
  selectedIds: T[];
  onChange: (ids: T[]) => void;
  placeholder: string;
  disabled?: boolean;
  disabledMessage?: string;
  emptyMessage?: string;
  theme: 'light' | 'dark' | 'high-contrast';
}

// Inline disclosure-style multi-select: trigger summarizes the picked count;
// open state shows a scrollable checkbox list (with search when there are
// enough options to warrant it). Inline rather than floating because the host
// modals already manage their own scroll container and positioning a floating
// popover inside one tends to clip; pushing content down is the simpler and
// more reliable choice.
export function MultiSelectDropdown<T extends Id = number>({
  options,
  selectedIds,
  onChange,
  placeholder,
  disabled = false,
  disabledMessage,
  emptyMessage,
  theme,
}: Props<T>): React.ReactElement {
  const isLight = theme !== 'dark';
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handlePointer = (e: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const showSearch = options.length > 10;
  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.name.toLowerCase().includes(q));
  }, [options, query]);

  const summary = (() => {
    if (disabled && disabledMessage) return disabledMessage;
    if (selectedIds.length === 0) return placeholder;
    if (selectedIds.length === 1) {
      const found = options.find((o) => o.id === selectedIds[0]);
      return found ? found.name : `${selectedIds.length} seleccionado`;
    }
    return `${selectedIds.length} seleccionados`;
  })();

  const isPlaceholder = selectedIds.length === 0 || (disabled && !!disabledMessage);

  const toggle = (id: T) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id],
    );
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        className={clsx(
          'w-full flex items-center justify-between gap-2 rounded-2xl border px-4 py-3 text-xs font-black transition-all outline-none',
          isLight
            ? 'bg-slate-50 border-slate-200 hover:bg-white text-slate-950'
            : 'bg-white/5 border-white/5 hover:bg-white/10 text-white',
          disabled && 'opacity-50 cursor-not-allowed hover:bg-transparent',
          open && (isLight ? 'bg-white border-indigo-300 ring-4 ring-indigo-100' : 'bg-white/10 border-indigo-500/50'),
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span
          className={clsx(
            'truncate text-left',
            isPlaceholder && (isLight ? 'text-slate-400 font-bold' : 'text-slate-500 font-bold'),
          )}
        >
          {summary.toUpperCase()}
        </span>
        <ChevronDown
          size={14}
          className={clsx('shrink-0 transition-transform text-slate-400', open && 'rotate-180')}
        />
      </button>

      {open && !disabled && (
        <div
          className={clsx(
            'mt-2 rounded-2xl border shadow-lg overflow-hidden',
            isLight ? 'bg-white border-slate-200' : 'bg-slate-900 border-white/10',
          )}
          role="listbox"
        >
          {showSearch && (
            <div
              className={clsx(
                'flex items-center gap-2 px-3 py-2 border-b',
                isLight ? 'border-slate-100 bg-slate-50' : 'border-white/5 bg-white/5',
              )}
            >
              <Search size={14} className="text-slate-400 shrink-0" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar..."
                className={clsx(
                  'bg-transparent border-none outline-none text-xs font-medium w-full placeholder:text-slate-400',
                  isLight ? 'text-slate-900' : 'text-white',
                )}
                autoFocus
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="text-slate-400 hover:text-slate-600 shrink-0"
                  aria-label="Limpiar búsqueda"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          )}

          <div className="max-h-64 overflow-y-auto custom-scrollbar">
            {options.length === 0 ? (
              <p className={clsx('text-[11px] italic px-4 py-3', isLight ? 'text-slate-400' : 'text-slate-500')}>
                {emptyMessage ?? 'Sin opciones disponibles.'}
              </p>
            ) : filtered.length === 0 ? (
              <p className={clsx('text-[11px] italic px-4 py-3', isLight ? 'text-slate-400' : 'text-slate-500')}>
                Sin resultados para "{query}".
              </p>
            ) : (
              filtered.map((o) => {
                const selected = selectedIds.includes(o.id);
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => toggle(o.id)}
                    className={clsx(
                      'w-full flex items-center gap-3 px-4 py-2.5 text-left text-xs transition-colors',
                      isLight ? 'hover:bg-slate-50' : 'hover:bg-white/5',
                      selected && (isLight ? 'bg-indigo-50/60' : 'bg-indigo-500/10'),
                    )}
                    role="option"
                    aria-selected={selected}
                  >
                    <span
                      className={clsx(
                        'w-4 h-4 rounded border flex items-center justify-center shrink-0',
                        selected
                          ? 'bg-indigo-600 border-indigo-600 text-white'
                          : isLight
                            ? 'bg-white border-slate-300'
                            : 'bg-white/5 border-white/20',
                      )}
                    >
                      {selected && <Check size={11} strokeWidth={3} />}
                    </span>
                    <span
                      className={clsx(
                        'flex-1 font-medium truncate',
                        selected
                          ? isLight ? 'text-indigo-700 font-bold' : 'text-indigo-300 font-bold'
                          : isLight ? 'text-slate-700' : 'text-slate-300',
                      )}
                    >
                      {o.name}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
